import { Fragment, type ReactNode } from 'react'
import clsx from 'clsx'

import { parseAnswer, type Run } from '../../lib/answer-markup'

// ============================================================================
// THE ANSWER'S TEXT, in the shape the model gave it — paragraphs, bullets, numbered items, bold,
// and short bold labels. See lib/answer-markup.ts for what is allowed and why. One component for
// the desktop conversation and the phone, so the two never disagree about what a list looks like.
//
// `stream` wraps every clause (a list item; a sentence of a paragraph) in a `[data-clause]` span,
// which is what Message.tsx animates in reading order (13e). Off, it is the plain elements.
// ============================================================================

export function AnswerBody({
  text,
  stream = false,
  className,
}: {
  text: string
  stream?: boolean
  className?: string
}) {
  const blocks = parseAnswer(text)
  return (
    <div className={clsx('flex flex-col gap-[7px]', className)}>
      {blocks.map((block, i) =>
        block.kind === 'list' ? (
          <ListBlock key={i} ordered={block.ordered} items={block.items} stream={stream} />
        ) : (
          <p key={i} className={clsx(block.label && 'mt-[3px] font-semibold')}>
            {block.label ? (
              <Clause stream={stream}>{runs(block.runs)}</Clause>
            ) : (
              sentences(block.runs).map((sentence, j, all) => (
                <Fragment key={j}>
                  <Clause stream={stream}>{runs(sentence)}</Clause>
                  {j < all.length - 1 ? ' ' : ''}
                </Fragment>
              ))
            )}
          </p>
        ),
      )}
    </div>
  )
}

function ListBlock({ ordered, items, stream }: { ordered: boolean; items: Run[][]; stream: boolean }) {
  const Tag = ordered ? 'ol' : 'ul'
  return (
    <Tag className={clsx('flex flex-col gap-[4px] pl-[20px]', ordered ? 'list-decimal' : 'list-disc')}>
      {items.map((item, i) => (
        <li key={i} className="pl-[2px]">
          <Clause stream={stream}>{runs(item)}</Clause>
        </li>
      ))}
    </Tag>
  )
}

function Clause({ stream, children }: { stream: boolean; children: ReactNode }) {
  return stream ? (
    <span data-clause className="opacity-0">
      {children}
    </span>
  ) : (
    <>{children}</>
  )
}

function runs(list: Run[]) {
  return list.map((run, i) =>
    run.bold ? (
      <strong key={i} className="font-semibold">
        {run.text}
      </strong>
    ) : (
      <Fragment key={i}>{run.text}</Fragment>
    ),
  )
}

/**
 * A paragraph's runs, cut at sentence ends so each sentence can arrive on its own beat. The cut
 * is the same one `splitClauses` makes, applied across bold boundaries without breaking them.
 */
function sentences(list: Run[]): Run[][] {
  const out: Run[][] = [[]]
  for (const run of list) {
    const parts = run.text.split(/(?<=[.!?])\s+/)
    parts.forEach((part, i) => {
      if (i > 0) out.push([])
      if (part) out[out.length - 1]!.push({ text: part, bold: run.bold })
    })
  }
  return out.filter((s) => s.length > 0)
}
